import { z } from 'zod';
import { cardPaymentSchema } from './billing';
import type { AutoPayDto, ChargeQuoteDto, InvoiceDto } from './billing';
import type { TopupOptionDto } from './customers';
import { passwordSchema } from './users';

/**
 * The customer portal (switch `customer-portal`): a company's customers sign in on its own page,
 * /portal/<company>, and pay, renew and top up by themselves. What it offers is what the
 * reference's Customer Portal settings switch (SC319 to SC327); its screens were not seen, so their
 * layout and the sign-in by telephone number and password are assumptions.
 */

const mdn = z
  .string()
  .trim()
  .regex(/^\d{10}$/, 'Telephone Number must be 10 digits');

export const portalLoginSchema = z.object({
  mdn,
  password: z.string().min(1, 'Password is required').max(200),
});
export type PortalLoginInput = z.input<typeof portalLoginSchema>;

export const portalForgotSchema = z.object({ mdn });
export type PortalForgotInput = z.input<typeof portalForgotSchema>;

export const portalPasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current Password is required').max(200),
  newPassword: passwordSchema,
});
export type PortalPasswordInput = z.input<typeof portalPasswordSchema>;

/** How a customer pays here: a card, or what is in their wallet. */
const payment = {
  method: z.enum(['card', 'wallet'], { errorMap: () => ({ message: 'Select how to pay' }) }),
  card: cardPaymentSchema.optional(),
};
const needsCard = (v: { method: string; card?: unknown }, ctx: z.RefinementCtx) => {
  if (v.method === 'card' && !v.card)
    ctx.addIssue({ code: 'custom', path: ['card'], message: 'Enter the card' });
};
const planId = z.string().regex(/^[a-f\d]{24}$/i, 'Select Plan');

export const portalRenewSchema = z.object(payment).superRefine(needsCard);
export const portalPlanChangeSchema = z.object({ planId, ...payment }).superRefine(needsCard);
export const portalTopupSchema = z
  .object({ kind: z.enum(['minutes', 'data']), topupId: planId, ...payment })
  .superRefine(needsCard);
export const portalWalletSchema = z.object({
  amountCents: z
    .number({ invalid_type_error: 'Amount is required' })
    .int()
    .min(100, 'Enter at least $1.00')
    .max(50_000, 'Enter at most $500.00'),
  card: cardPaymentSchema,
});
export const portalPayInvoicesSchema = z
  .object({
    amountCents: z.number({ invalid_type_error: 'Amount is required' }).int().min(1),
    ...payment,
  })
  .superRefine(needsCard);
export const portalQuoteSchema = z.object({
  what: z.enum(['renew', 'change', 'minutes', 'data']),
  id: z.string().optional(),
  method: z.enum(['card', 'wallet']).default('card'),
});

export interface PortalCompanyDto {
  name: string;
  logoUrl: string;
}

/** The customer's own page. Each part is present only while its setting allows it. */
export interface PortalHomeDto {
  mustChangePassword: boolean;
  customer: {
    firstName: string;
    lastName: string;
    accountNo: string;
    mdn: string;
    status: string;
    planName: string;
    planPriceCents: number;
    planExpiresAt: string | null;
    walletBalanceCents: number;
    email: string;
    address: string;
  };
  /** "Enable Line Details" (SC325). */
  line: { sim: string; imei: string; carrier: string } | null;
  autopay: AutoPayDto | null;
  amountDueCents: number;
  /** "Enable Unpaid Invoice" (SC323). */
  unpaidInvoices: InvoiceDto[] | null;
  /** "Show All Invoices" (SC326). */
  invoices: InvoiceDto[] | null;
  offers: {
    renew: boolean;
    changePlan: boolean;
    topup: boolean;
    addWallet: boolean;
    autopay: boolean;
    cards: boolean;
  };
  /** Why renewing is refused now, if it is. */
  renewBlocked: string | null;
}

export interface PortalOptionsDto {
  plans: Array<{ id: string; name: string; priceCents: number }>;
  minutes: TopupOptionDto[];
  data: TopupOptionDto[];
}

export type PortalQuoteDto = ChargeQuoteDto;
